import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Mapa decorativo del login (capa 1 del backdrop, según la maqueta):
 * Leaflet + tiles CARTO Voyager centrado en Pozuelo de Alarcón, totalmente
 * NO interactivo (sin zoom, drag ni teclado) y con una deriva ambiental lenta.
 *
 * Ocupa TODO el viewport (fixed inset-0). Es un mapa REAL de teselas, no una
 * imagen; para que no salga "cortado" recalculamos el tamaño con un
 * ResizeObserver (Leaflet necesita saber el tamaño real del contenedor, que en
 * el primer render puede ser 0), además de en window.resize y varios ticks.
 */
const POZUELO: [number, number] = [40.4357, -3.8136];

export function LoginMap() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const map = L.map(el, {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      fadeAnimation: false,
    }).setView(POZUELO, 14);

    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
      { maxZoom: 20, subdomains: 'abcd' },
    ).addTo(map);

    const refresh = () => map.invalidateSize(false);
    // Recalcular en cuanto el contenedor tenga tamaño real y en cada resize.
    const ro = new ResizeObserver(refresh);
    ro.observe(el);
    window.addEventListener('resize', refresh);
    const ticks = [0, 60, 150, 400, 900].map((t) => window.setTimeout(refresh, t));

    // Deriva ambiental lenta (igual que la maqueta).
    let a = 0;
    const drift = window.setInterval(() => {
      a += 0.0015;
      map.panTo(
        [POZUELO[0] + Math.sin(a) * 0.004, POZUELO[1] + Math.cos(a) * 0.004],
        { animate: true, duration: 2.4, easeLinearity: 0.5 },
      );
    }, 2600);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', refresh);
      ticks.forEach(window.clearTimeout);
      window.clearInterval(drift);
      map.remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 h-full w-full"
      style={{ filter: 'saturate(1.02) brightness(1.02)', background: '#eef1ef' }}
      aria-hidden="true"
    />
  );
}
